import axios, {AxiosError} from "axios"
import {logApiCall} from "@/components/datadog/utils";
import {
  RewardProgramsResponse,
  ProgramLogsResponse,
  ProgramRewardTransactionResponse,
  ProgramMetricsResponse,
  SingleTransactionResponse, RewardProgramResponse
} from "@/types/reward-program";
import {
  RewardInvitationResponse,
  RewardInviteSignedPayload,
  RewardInviteAcceptResponse,
  RewardInviteRejectResponse,
} from "@/types/redeem";

// Create axios instance with default config
export const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Bridge API instance
export const bridgeApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Bridge order types
export interface CreateBridgeOrderRequest {
  trigger_value_native: string // Amount in wei (as string to handle large numbers)
  solana_destination_address: string
}

export interface BridgeOrder {
  created_at: string
  payment_link: string
  id: string
  is_active: boolean
  solana_destination_address: string
  solana_spl_token_amount_to_send: number
  solana_spl_token_decimals: number
  solana_spl_token_mint: string
  trigger_chain: string
  trigger_value_native: string
}

export interface BridgeOrderResponse {
  data: BridgeOrder
}


// Add auth token to requests
api.interceptors.request.use((config) => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem("auth_token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
})

// API response types
export interface User {
  id: string
  username: string
  user_name: string
  description: string
  profile_image: string
  wallets: {
    urt: string
    usbc: string
  }
  balances: {
    urt: number
    usbc: number
  }
  badgeCompliance: boolean
  uns_profile_url: string
}

export interface MenuItem {
  id: string
  name: string
  path: string
  icon: string
}

export interface Transaction {
  id: string
  type: "Royalty payment" | "Cashback" | "Reward" | "Interest payment" | "Purchase"
  amount: number
  date: string
  memo: string
  direction: "in" | "out"
}

export interface TransactionResponse {
  transactions: Transaction[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface ConversionRate {
  urt_to_usbc: number
  usbc_to_urt: number
  fee_percentage: number
}

export interface CreatePaymentIntentInterface {
  trigger_value_native: number
  solana_destination_address: string
  bridge_type: string
}

export interface CreateUsbcPayBridgeOrderRequest {
  usbc_amount: number
}

export interface UsbcPayBridgeOrderResponse {
  data: string;
  requested_on: string
}

export enum UsbcPayStatus {
  CREATED = "CREATED",
  SCANNED = "SCANNED",
  READY_TO_PAY = "READY_TO_PAY",
  PAID = "PAID",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED"
}

export interface UsbcPayStatusMetadata {
  [key: string]: {
    message: string;
    timestamp: number;
  };
}

export interface UsbcPayStatusResponse {
  data: {
    created_at: number;
    id: string;
    metadata: UsbcPayStatusMetadata;
    status: UsbcPayStatus;
    updated_at: number;
  };
  requested_on: string;
}

export interface CreatePaymentIntentResponseInterface {
  data: {
    id: string
    amount: number,
    currency: string,
    client_secret: string,
    description: string,
    request_metadata: Record<string, any>
    worth_amount: number
    processing_fees: number
    fees_explanation: string
  }
}

export interface UpdateBridgeOrderRequest {
  solana_address: string
  txn_hash: string
  bridge_type: string
  usbc_address: string
}

export interface UpdateUsbcPayBridgeOrderRequest {
  txn_hash: string
  usbc_address: string
}

// API functions
export const apiService = {
  // Auth
  connectWallet: async (address: string, signature: string) => {
    const { data } = await api.post("/auth/wallet-connect", { address, signature })
    return data
  },

  // User
  getCurrentUser: async () => {
    //const { data } = await api.get<User>("/user/me")
    //return data
    return {}
  },

  // Menu
  getMenuItems: async () => {
    //const { data } = await api.get<MenuItem[]>("/menu")
    //return data
    return [
      {
        id: 'dashboard',
        name: 'Dashboard',
        path: '/dashboard',
        icon: 'home',
      },
      {
        id: "rewardPrograms",
        name: "Reward Programs",
        path: "/reward-programs",
        icon: "rewardPrograms",
      },
      {
        id: "buySell",
        name: "URT Top-Ups",
        path: "/buy-sell",
        icon: "buySell",
      },
    ]
  },

  // Transactions
  getTransactions: async (params: {
    page?: number
    pageSize?: number
    type?: string
    direction?: string
    startDate?: string
    endDate?: string
    search?: string
  }) => {
    const { data } = await api.get<TransactionResponse>("/transactions", { params })
    return data
  },

  // Conversion
  getConversionRates: async () => {
    //const { data } = await api.get<ConversionRate>("/conversion/rates")
    //return data
    return {
      urt_to_usbc: 0.01,
      usbc_to_urt: 100,
      usd_to_urt: 100,
      fee_percentage: 0,
    }
  },

  convertCurrency: async (params: {
    fromCurrency: string
    toCurrency: string
    amount: number
    fromWallet: string
    toWallet: string
  }) => {
    const { data } = await api.post("/conversion/convert", params)
    return data
  },

  // Bridge API
  createBridgeOrder: async (params: CreateBridgeOrderRequest): Promise<BridgeOrderResponse> => {
    try {
      const { data } = await bridgeApi.post<BridgeOrderResponse>("/v1/bridge-orders", params)
      logApiCall(
          "/v1/bridge-orders",
          "POST",
          params,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          "/v1/bridge-orders",
          "POST",
          params,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }

      )
      throw e
    }
  },

  getBridgeOrder: async (orderId: string, usbcAddress?: string): Promise<BridgeOrderResponse> => {
    try {
      const { data } = await bridgeApi.get<BridgeOrderResponse>(`/v1/bridge-orders/${orderId}`, {
        params: usbcAddress && {usbc_address: usbcAddress},
      })
      logApiCall(
          `/v1/bridge-orders/${orderId}`,
          "GET",
          undefined,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/bridge-orders/${orderId}`,
          "GET",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }

      )
      throw e
    }
  },

  createStripeOrder: async (payload: CreatePaymentIntentInterface): Promise<CreatePaymentIntentResponseInterface> => {
    try {
      const { data } = await bridgeApi.post<CreatePaymentIntentResponseInterface>(`/v1/stripe-payment-intent`, payload)
      logApiCall(
          `/v1/stripe-payment-intent`,
          "POST",
          payload,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/stripe-payment-intent`,
          "POST",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }

      )
      throw e
    }
  },

  // Bridge API
  createUsbcPayBridgeOrder: async (params: CreateUsbcPayBridgeOrderRequest): Promise<UsbcPayBridgeOrderResponse> => {
    try {
      const { data } = await bridgeApi.post<UsbcPayBridgeOrderResponse>("/v1/usbc-pay-intent", params)
      logApiCall(
          "/v1/usbc-pay-intent",
          "POST",
          params,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          "/v1/usbc-pay-intent",
          "POST",
          params,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }

      )
      throw e
    }
  },

  updateBridgeOrder: async (orderId: string, payload: UpdateBridgeOrderRequest): Promise<BridgeOrderResponse> => {
    try {
      const { data } = await bridgeApi.put<BridgeOrderResponse>(`/v1/bridge-orders/${orderId}`, payload)
      logApiCall(
          `/v1/bridge-orders/${orderId}`,
          "PUT",
          payload,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/bridge-orders/${orderId}`,
          "PUT",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }

      )
      throw e
    }
  },

  updateUsbcPayBridgeOrder: async (orderId: string, payload: UpdateUsbcPayBridgeOrderRequest): Promise<BridgeOrderResponse> => {
    try {
      const { data } = await bridgeApi.put<BridgeOrderResponse>(`/v1/bridge-orders/usbc-pay-intent/${orderId}`, payload)
      logApiCall(
          `/v1/bridge-orders/usbc-pay-intent/${orderId}`,
          "PUT",
          payload,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/bridge-orders/usbc-pay-intent/${orderId}`,
          "PUT",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }

      )
      throw e
    }
  },

  getUsbcPayStatus: async (orderId: string): Promise<UsbcPayStatusResponse> => {
    try {
      const { data } = await bridgeApi.get<UsbcPayStatusResponse>(`/v1/bridge-orders/usbc-pay-intent/${orderId}`)
      logApiCall(
          `/v1/bridge-orders/usbc-pay-intent/${orderId}`,
          "GET",
          undefined,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/bridge-orders/usbc-pay-intent/${orderId}`,
          "GET",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }
      )
      throw e
    }
  },

  // Reward Programs API
  getRewardPrograms: async (urtAddress: string): Promise<RewardProgramsResponse> => {
    try {
      const { data } = await bridgeApi.get<RewardProgramsResponse>("/v1/rewards", {
        params: { urt_address: urtAddress },
      })
      logApiCall(
          "/v1/rewards",
          "GET",
          undefined,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          "/v1/rewards",
          "GET",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }
      )
      throw e
    }
  },

  // Reward Programs API
  getRewardProgramById: async (programId:string, urtAddress: string): Promise<RewardProgramResponse> => {
    try {
      const { data } = await bridgeApi.get<RewardProgramResponse>(`/v1/rewards/${programId}`, {
        params: { urt_address: urtAddress },
      })
      logApiCall(
          `/v1/rewards/${programId}`,
          "GET",
          undefined,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/rewards/${programId}`,
          "GET",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }
      )
      throw e
    }
  },

  getProgramLogs: async (rewardProgramId: string, userId: string): Promise<ProgramLogsResponse> => {
    try {
      const { data } = await bridgeApi.get<ProgramLogsResponse>(`/v1/rewards/${rewardProgramId}/logs`, {
        params: { user_id: userId },
      })
      logApiCall(
          `/v1/rewards/${rewardProgramId}/logs`,
          "GET",
          undefined,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/rewards/${rewardProgramId}/logs`,
          "GET",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }
      )
      throw e
    }
  },

  getUserTransactions: async (urtAddress: string, page: number, perPage: number): Promise<ProgramRewardTransactionResponse> => {
    try {
      const { data } = await bridgeApi.get<ProgramRewardTransactionResponse>(`/v1/rewards/transactions`, {
        params: { urt_address: urtAddress, page, per_page: perPage },
      })
      logApiCall(
          `/v1/rewards/transactions`,
          "GET",
          undefined,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/rewards/transactions`,
          "GET",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }
      )
      throw e
    }
  },

  getProgramMetrics: async (rewardProgramId: string, userId: string): Promise<ProgramMetricsResponse> => {
    try {
      const { data } = await bridgeApi.get<ProgramMetricsResponse>(`/v1/rewards/${rewardProgramId}/metrics`, {
        params: { user_id: userId },
      })
      logApiCall(
          `/v1/rewards/${rewardProgramId}/metrics`,
          "GET",
          undefined,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/rewards/${rewardProgramId}/metrics`,
          "GET",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }
      )
      throw e
    }
  },

  getTransactionById: async (transactionId: string): Promise<SingleTransactionResponse> => {
    try {
      const { data } = await bridgeApi.get<SingleTransactionResponse>(`/v1/rewards/transactions/${transactionId}`)
      logApiCall(
          `/v1/rewards/transactions/${transactionId}`,
          "GET",
          undefined,
          data,
          undefined,
          undefined
      )
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(
          `/v1/rewards/transactions/${transactionId}`,
          "GET",
          undefined,
          error.response?.data,
          undefined,
          {
            name: error.name,
            message: error.message,
            stack: JSON.stringify(error.response?.data) ?? error.stack
          }
      )
      throw e
    }
  },

  // Reward Invitations API (public, signature-gated)
  getRewardInvitation: async (claimUlid: string): Promise<RewardInvitationResponse> => {
    const path = `/v1/reward-invitations/${claimUlid}`
    try {
      const { data } = await bridgeApi.get<RewardInvitationResponse>(path)
      logApiCall(path, "GET", undefined, data, undefined, undefined)
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(path, "GET", undefined, error.response?.data, undefined, {
        name: error.name,
        message: error.message,
        stack: JSON.stringify(error.response?.data) ?? error.stack,
      })
      throw e
    }
  },

  acceptRewardInvitation: async (
    claimUlid: string,
    payload: RewardInviteSignedPayload,
  ): Promise<RewardInviteAcceptResponse> => {
    const path = `/v1/reward-invitations/${claimUlid}/accept`
    try {
      const { data } = await bridgeApi.post<RewardInviteAcceptResponse>(path, payload)
      logApiCall(path, "POST", payload, data, undefined, undefined)
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(path, "POST", payload, error.response?.data, undefined, {
        name: error.name,
        message: error.message,
        stack: JSON.stringify(error.response?.data) ?? error.stack,
      })
      throw e
    }
  },

  rejectRewardInvitation: async (
    claimUlid: string,
    payload: RewardInviteSignedPayload,
  ): Promise<RewardInviteRejectResponse> => {
    const path = `/v1/reward-invitations/${claimUlid}/reject`
    try {
      const { data } = await bridgeApi.post<RewardInviteRejectResponse>(path, payload)
      logApiCall(path, "POST", payload, data, undefined, undefined)
      return data
    } catch (e) {
      const error: AxiosError = e as AxiosError
      logApiCall(path, "POST", payload, error.response?.data, undefined, {
        name: error.name,
        message: error.message,
        stack: JSON.stringify(error.response?.data) ?? error.stack,
      })
      throw e
    }
  },
}
